import React, { useState, useEffect } from 'react';
import { dashApi, formatAmount } from '../../api/dashApi';
import api from '../../api/axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getModuleDetails(row) {
  if (!row) return null;
  const {
    MultilevelApproval, MakerLogin, GenericReportFlag,
    AmountBasedTransactionAllowed, MultilevelGroupInvisible,
    MultilevelGroup, IndividualCheckerFunctionality,
    NoOfReportAmountRange, NoOfApprovalLevel, MakerActionPending,
  } = row;
  if (!MultilevelApproval && !MakerLogin && !GenericReportFlag && !AmountBasedTransactionAllowed)
    return 'Module 1';
  if (MultilevelApproval && MultilevelGroupInvisible)
    return 'Module 2';
  if ((MultilevelGroup && MultilevelApproval && IndividualCheckerFunctionality) || (MultilevelGroup && MultilevelApproval))
    return 'Module 3';
  if (IndividualCheckerFunctionality && MultilevelGroup && MultilevelApproval && MakerLogin)
    return 'Module 4';
  if (GenericReportFlag && MakerLogin && NoOfReportAmountRange > 0 && NoOfApprovalLevel > 0)
    return 'Module 5';
  if (AmountBasedTransactionAllowed && NoOfReportAmountRange > 0 && NoOfApprovalLevel > 0 && MakerActionPending)
    return 'Module 2.1';
  return null;
}

function formatColName(col) {
  const stripped = col.replace(/^(Is|Has|Can|Allow|Enable)/i, '') || col;
  return stripped
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d+)/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase())
    || col;
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ enterprise, onBillingDateSaved }) {
  const [editing, setEditing] = useState({ open: false, value: '', saving: false, error: '' });

  const openEdit = () => {
    const current = enterprise.billingStartDate
      ? enterprise.billingStartDate.substring(0, 10)
      : (enterprise.effectiveBillingStartDate ? enterprise.effectiveBillingStartDate.substring(0, 10) : '');
    setEditing({ open: true, value: current, saving: false, error: '' });
  };

  const handleSave = async (resetToDefault = false) => {
    const newDate = resetToDefault ? null : (editing.value || null);
    setEditing(s => ({ ...s, saving: true, error: '' }));
    try {
      await api.post(`/api/admin/enterprises/${enterprise.enterpriseId}/billing-start-date`, {
        billingStartDate: newDate,
      });
      setEditing({ open: false, value: '', saving: false, error: '' });
      if (onBillingDateSaved) onBillingDateSaved();
    } catch (e) {
      setEditing(s => ({ ...s, saving: false, error: e.response?.data?.error || 'Failed to save.' }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Enterprise ID */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Enterprise ID</label>
        <p className="text-sm text-gray-800 font-mono">{enterprise.enterpriseId}</p>
      </div>

      {/* Enterprise Name */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Enterprise Name</label>
        <p className="text-sm text-gray-800">{enterprise.enterpriseName}</p>
      </div>

      {/* Created On */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Created On (ORID)</label>
        <p className="text-sm text-gray-800">{formatDate(enterprise.createdOn)}</p>
      </div>

      {/* Billing Start Date */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Billing Start Date</label>
        {!editing.open ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-gray-800">
              {enterprise.billingStartDate
                ? <span className="text-orange-600 font-medium">{formatDate(enterprise.billingStartDate)} <span className="text-xs text-orange-400">(custom)</span></span>
                : <span>{formatDate(enterprise.effectiveBillingStartDate)} <span className="text-xs text-gray-400">(default — same as Created On)</span></span>
              }
            </p>
            <button onClick={openEdit} className="text-xs text-blue-600 hover:underline">Edit</button>
          </div>
        ) : (
          <div className="space-y-2">
            <input
              type="date"
              value={editing.value}
              min={enterprise.createdOn ? enterprise.createdOn.substring(0, 10) : undefined}
              onChange={ev => setEditing(s => ({ ...s, value: ev.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {editing.error && <p className="text-xs text-red-500">{editing.error}</p>}
            <p className="text-xs text-gray-400">Must be on or after Created On date. Leave blank to reset to default.</p>
            <div className="flex gap-2">
              <button onClick={() => handleSave(false)} disabled={editing.saving}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {editing.saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing({ open: false, value: '', saving: false, error: '' })}
                className="px-3 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              {enterprise.billingStartDate && (
                <button onClick={() => handleSave(true)}
                  className="px-3 py-1.5 border border-orange-300 text-orange-600 text-xs rounded-lg hover:bg-orange-50">
                  Reset to Default
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Billing Configuration */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Billing Configuration</label>
        {enterprise.hasBillingConfig
          ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Configured</span>
          : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">Not Set</span>
        }
      </div>

      {/* Status */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Status</label>
        <p className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block ${
          enterprise.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
        }`}>
          {enterprise.active ? 'Active' : 'Inactive'}
        </p>
      </div>

      {/* Assigned KAMs */}
      {enterprise.kams && enterprise.kams.length > 0 && (
        <div>
          <label className="block text-xs text-gray-400 mb-2">Assigned KAMs</label>
          <div className="flex flex-wrap gap-2">
            {enterprise.kams.map(k => (
              <span key={k.kamId} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {k.kamName || k.kamId}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Portal Details Tab ───────────────────────────────────────────────────────
function PortalDetailsTab({ enterpriseId, source }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    const fetcher = source === 'IFLEET' ? dashApi.getFleetEnterprises : dashApi.getEcmsEnterprises;
    fetcher()
      .then(res => {
        const list = res.data || [];
        const match = list.find(e =>
          (e.EnterpriseId || e.enterpriseId || '').toLowerCase() === (enterpriseId || '').toLowerCase()
        );
        setData(match || null);
        if (!match) setError('Enterprise not found in portal data.');
      })
      .catch(() => setError('Failed to load portal details.'))
      .finally(() => setLoading(false));
  }, [enterpriseId, source]);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (error)   return <p className="text-sm text-gray-400 text-center py-10">{error}</p>;
  if (!data)   return <p className="text-sm text-gray-400 text-center py-10">No data available.</p>;

  const fields = [
    ['Enterprise ID', data.EnterpriseId || data.enterpriseId],
    ['Name',          data.EnterpriseName || data.enterpriseName],
    ['City',          data.City  || data.city],
    ['State',         data.State || data.state],
    ['Email',         data.Email || data.email],
    ['Status',        data.Status || data.status],
    ['Created On',    formatDate(data.CreatedOn || data.createdOn)],
  ];

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 py-2">
      {fields.map(([label, val]) => (
        <div key={label}>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">{label}</p>
          {label === 'Status' ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              String(val).toLowerCase() === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${String(val).toLowerCase() === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
              {val || '—'}
            </span>
          ) : (
            <p className="text-sm text-gray-800">{val || '—'}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Summary card helper ──────────────────────────────────────────────────────
function SummaryCard({ title, labelKey, rows, totalLabel }) {
  const total = rows.reduce((s, r) => s + (Number(r.Count || r.count) || 0), 0);
  return (
    <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col">
      <h3 className="text-base font-bold text-gray-800 text-center mb-4">{title}</h3>
      <div className="flex-1 divide-y divide-gray-100">
        {rows.length === 0
          ? <p className="text-xs text-gray-400 text-center py-4">No data.</p>
          : rows.map((r, i) => {
            const label = r[labelKey] || r[labelKey.toLowerCase()] || '—';
            const count = Number(r.Count || r.count) || 0;
            return (
              <div key={i} className="flex items-center justify-between py-2.5">
                <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">{label}</span>
                <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-full bg-blue-100 text-primary text-sm font-semibold">
                  {count}
                </span>
              </div>
            );
          })
        }
      </div>
      <div className="pt-3 mt-2 border-t border-gray-100 text-center">
        <span className="text-sm font-bold text-primary">{totalLabel}: {total}</span>
      </div>
    </div>
  );
}

// ─── Team Members Tab ─────────────────────────────────────────────────────────
function TeamMembersTab({ enterpriseId, memberSummaryFetcher, userSummaryFetcher }) {
  const [memberRows, setMemberRows] = useState([]);
  const [userRows,   setUserRows]   = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([memberSummaryFetcher(enterpriseId), userSummaryFetcher(enterpriseId)])
      .then(([mRes, uRes]) => {
        setMemberRows(mRes.data || []);
        setUserRows(uRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [enterpriseId, memberSummaryFetcher, userSummaryFetcher]);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="flex gap-4 py-2">
      <SummaryCard title="Team Members" labelKey="Status" rows={memberRows} totalLabel="Total Members" />
      <SummaryCard title="Users"        labelKey="Role"   rows={userRows}   totalLabel="Total Roles" />
    </div>
  );
}

// ─── Module Settings Tab ──────────────────────────────────────────────────────
function ModuleSettingsTab({ enterpriseId, funcFetcher, typesFetcher, updateFn }) {
  const [rowData,  setRowData]  = useState({});
  const [boolCols, setBoolCols] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);

  useEffect(() => {
    Promise.all([funcFetcher(enterpriseId), typesFetcher()])
      .then(([funcRes, typesRes]) => {
        const row = (funcRes.data || [])[0] || {};
        setRowData(row);
        const cols = (typesRes.data || [])
          .filter(t => (t.Type || t.type) === 'bool')
          .map(t => t.Field || t.field)
          .filter(col => col && col.toLowerCase() !== 'enterpriseid');
        setBoolCols(cols);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [enterpriseId, funcFetcher, typesFetcher]);

  const toggle = (col) => setRowData(prev => ({ ...prev, [col]: prev[col] ? 0 : 1 }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFn({ enterpriseId, functionalities: [rowData] });
      setToast({ type: 'success', msg: 'Settings saved successfully.' });
    } catch {
      setToast({ type: 'error', msg: 'Failed to save settings.' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (boolCols.length === 0) return <p className="text-sm text-gray-400 text-center py-10">No modules found.</p>;

  const moduleLabel = getModuleDetails(rowData);

  return (
    <div className="space-y-4">
      {moduleLabel && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/10 border border-primary/20">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Module</span>
          <span className="px-3 py-1 rounded-full bg-primary text-white text-sm font-bold">{moduleLabel}</span>
        </div>
      )}
      {toast && (
        <div className={`px-3 py-2 rounded text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
        }`}>{toast.msg}</div>
      )}
      <div className="grid grid-cols-1 gap-2">
        {boolCols.map(col => {
          const enabled = !!(rowData[col]);
          return (
            <div key={col} className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-gray-50 hover:bg-white transition-colors">
              <span className="text-sm text-gray-700 font-medium">{formatColName(col)}</span>
              <button
                onClick={() => toggle(col)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex justify-end pt-2">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Virtual Accounts Tab ─────────────────────────────────────────────────────
function VirtualAccountsTab({ enterpriseId, vaFetcher, poolFetcher, pfFetcher }) {
  const [accounts,   setAccounts]   = useState([]);
  const [pool,       setPool]       = useState([]);
  const [pf,         setPf]         = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showTrends, setShowTrends] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([vaFetcher(enterpriseId), poolFetcher(enterpriseId), pfFetcher(enterpriseId)])
      .then(([vaRes, poolRes, pfRes]) => {
        const vaData = vaRes.data;
        setAccounts(Array.isArray(vaData) ? vaData : vaData ? [vaData] : []);
        setPool(Array.isArray(poolRes.data) ? poolRes.data : []);
        setPf(Array.isArray(pfRes.data) ? pfRes.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [enterpriseId, vaFetcher, poolFetcher, pfFetcher]);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const currentPool = pool.length ? pool[pool.length - 1] : null;
  const currentPf   = pf.length   ? pf[pf.length - 1]     : null;
  const chartData   = pool.map(r => ({ label: r.Month || r.month || '', amount: r.Amount || r.amount || 0 }));

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        {/* Virtual Account Numbers */}
        <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-sm font-bold text-gray-800 text-center mb-3">Virtual Account Numbers</h4>
          {accounts.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No virtual accounts found.</p>
          ) : (
            <div className="space-y-2">
              {accounts.map((a, i) => (
                <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-1">
                  <p><span className="font-semibold text-gray-700">Account Number:</span> <span className="font-mono text-gray-800">{a.AccountNumber || '—'}</span></p>
                  <p><span className="font-semibold text-gray-700">Balance:</span> <span className="text-gray-800">{a.FormattedBalance || formatAmount(a.Balance) || '—'}</span></p>
                  <p><span className="font-semibold text-gray-700">IFSC Code:</span> <span className="text-gray-800">{a.IFSCCode || '—'}</span></p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Month Details */}
        <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
          <h4 className="text-sm font-bold text-gray-800 text-center mb-3">Current Month Details</h4>
          <div className="space-y-2 flex-1">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <span className="font-semibold text-gray-700">Pool Account Loading: </span>
              <span className="text-gray-800">{currentPool ? formatAmount(currentPool.Amount || currentPool.amount) : '—'}</span>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              <span className="font-semibold text-gray-700">Platform Fees: </span>
              <span className="text-gray-800">{currentPf ? formatAmount(currentPf.Amount || currentPf.amount) : '—'}</span>
            </div>
          </div>
          <button onClick={() => setShowTrends(v => !v)}
            className="mt-4 w-full py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors">
            {showTrends ? 'Hide Trends' : 'See Trends'}
          </button>
        </div>
      </div>

      {showTrends && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h4 className="text-sm font-bold text-gray-800 text-center mb-4">Monthly Trend Of Pool Account Loading</h4>
          {chartData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No trend data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(1)} Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)} L` : v}
                />
                <Tooltip
                  cursor={{ fill: '#f3f4f6' }}
                  formatter={(v) => [formatAmount(v), 'Pool Loading']}
                  labelStyle={{ fontWeight: 600, color: '#111827' }}
                />
                <Bar dataKey="amount" fill="#4B70E2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────
const DETAIL_TABS = ['Overview', 'Portal Details', 'Team Members', 'Module Settings', 'Virtual Accounts'];

const EnterpriseDetailPanel = ({ enterprise, source = 'ECMS', onBillingDateSaved }) => {
  const [tab, setTab] = useState('Overview');

  // Reset to Overview when a different enterprise is opened
  useEffect(() => { setTab('Overview'); }, [enterprise?.enterpriseId]);

  const oridId = enterprise?.enterpriseId;

  // Select API methods based on source
  const a = source === 'IFLEET' ? {
    getMemberSummary:    dashApi.getFleetMemberSummary,
    getUserSummary:      dashApi.getFleetUserSummary,
    getFunctionality:    dashApi.getFleetFunctionality,
    getFuncTypes:        dashApi.getFleetFuncTypes,
    updateFunctionality: dashApi.updateFleetFunctionality,
    getVirtualAccounts:  dashApi.getFleetVirtualAccounts,
    getPoolLoading:      dashApi.getFleetPoolLoading,
    getPlatformFee:      dashApi.getFleetPlatformFee,
  } : {
    getMemberSummary:    dashApi.getEcmsMemberSummary,
    getUserSummary:      dashApi.getEcmsUserSummary,
    getFunctionality:    dashApi.getEcmsFunctionality,
    getFuncTypes:        dashApi.getEcmsFuncTypes,
    updateFunctionality: dashApi.updateEcmsFunctionality,
    getVirtualAccounts:  dashApi.getEcmsVirtualAccounts,
    getPoolLoading:      dashApi.getEcmsPoolLoading,
    getPlatformFee:      dashApi.getEcmsPlatformFee,
  };

  if (!enterprise) return null;

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="flex border-b mb-4 -mx-1">
        {DETAIL_TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px ${
              tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {tab === 'Overview' && (
          <OverviewTab enterprise={enterprise} onBillingDateSaved={onBillingDateSaved} />
        )}
        {tab === 'Portal Details' && (
          <PortalDetailsTab enterpriseId={oridId} source={source} />
        )}
        {tab === 'Team Members' && (
          <TeamMembersTab
            enterpriseId={oridId}
            memberSummaryFetcher={a.getMemberSummary}
            userSummaryFetcher={a.getUserSummary}
          />
        )}
        {tab === 'Module Settings' && (
          <ModuleSettingsTab
            enterpriseId={oridId}
            funcFetcher={a.getFunctionality}
            typesFetcher={a.getFuncTypes}
            updateFn={a.updateFunctionality}
          />
        )}
        {tab === 'Virtual Accounts' && (
          <VirtualAccountsTab
            enterpriseId={oridId}
            vaFetcher={a.getVirtualAccounts}
            poolFetcher={a.getPoolLoading}
            pfFetcher={a.getPlatformFee}
          />
        )}
      </div>
    </div>
  );
};

export default EnterpriseDetailPanel;
